import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch } from 'react-redux';
import { restoreAuth } from './features/auth/authSlice';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';
import PublicLayout from './layouts/PublicLayout';

// Public Pages
import Login from './Pages/Login';
import Signup from './Pages/Signup';
import OTPSignup from './Pages/OTPSignup';
import VerifyOTP from './Pages/VerifyOTP';
import BookAppointment from './Pages/BookAppointment';
import HomePage from './Pages/HomePage';
import DoctorOnboarding from './Pages/DoctorOnboarding/DoctorOnboarding';
import EnhancedDoctorOnboarding from './Pages/DoctorOnboarding/EnhancedDoctorOnboarding.js';
import MagicLinkOnboarding from './Pages/DoctorOnboarding/MagicLinkOnboarding.js';
import UnifiedDoctorOnboardingFlow from './components/DoctorOnboarding/UnifiedDoctorOnboardingFlow';
import DoctorPublicProfile from './Pages/DoctorPublicProfile';

// Patient Onboarding Components
import OnboardingRouter from './components/PatientOnboarding/OnboardingRouter';
import UnifiedOnboardingFlow from './components/PatientOnboarding/UnifiedOnboardingFlow';
import MagicLinkHandler from './components/MagicLinkHandler';
import EmailVerification from './components/EmailVerification';
import PasswordReset from './components/PasswordReset';
import ForgotPassword from './components/ForgotPassword';
import SessionProvider from './providers/SessionProvider';

// Patient Dashboard Pages
import PatientOverview from './Pages/PatientDashboard/Overview';
import PatientAppointments from './Pages/PatientDashboard/Appointments';
import PatientConsultationHistory from './Pages/PatientDashboard/ConsultationHistory';
import PatientMedicalRecords from './Pages/PatientDashboard/MedicalRecords';
import PatientBilling from './Pages/PatientDashboard/Billing';
import PatientProfile from './Pages/PatientDashboard/Profile';
import PatientMessaging from './Pages/PatientDashboard/Messaging';
import PatientSupport from './Pages/PatientDashboard/Support';
import PatientFeedback from './Pages/PatientDashboard/Feedback';
import PatientNotifications from './Pages/PatientDashboard/Notifications';

// Patient Management Pages
import PatientManagement from './Pages/PatientDashboard/PatientManagement';
import MedicalHistory from './Pages/PatientDashboard/MedicalHistory';
import DocumentManagement from './Pages/PatientDashboard/DocumentManagement';
import ConsentManagement from './Pages/PatientDashboard/ConsentManagement';
import PrivacyControls from './Pages/PatientDashboard/PrivacyControls';

// Doctor Management Pages
import DoctorManagement from './Pages/DoctorDashboard/DoctorManagement';
import DoctorAnalytics from './Pages/DoctorDashboard/DoctorAnalytics';
import DoctorVerification from './Pages/DoctorDashboard/DoctorVerification';

// Clinic Management Pages
import ClinicManagement from './Pages/ClinicDashboard/ClinicManagement';
import ClinicAnalytics from './Pages/ClinicDashboard/ClinicAnalytics';

// Notification Management Pages
import NotificationManagement from './Pages/NotificationDashboard/NotificationManagement';

// Doctor Dashboard Pages
import DoctorOverview from './Pages/DoctorDashboard/Overview';
import DoctorAppointments from './Pages/DoctorDashboard/Appointments';
import DoctorPatientList from './Pages/DoctorDashboard/PatientList';
import DoctorPatientDetails from './Pages/DoctorDashboard/PatientDetails';
import DoctorConsultation from './Pages/DoctorDashboard/Consultation';
import EnhancedConsultation from './Pages/DoctorDashboard/EnhancedConsultation';
import EnhancedConsultationDashboard from './Pages/DoctorDashboard/EnhancedConsultationDashboard';
import DoctorNotes from './Pages/DoctorDashboard/Notes';
import DoctorConsultationHistory from './Pages/DoctorDashboard/ConsultationHistory';
import DoctorConsultationDetails from './Pages/DoctorDashboard/ConsultationDetails';
import DoctorPrescribe from './Pages/DoctorDashboard/Prescribe';
import DoctorAvailability from './Pages/DoctorDashboard/AvailabilityManager';
import DoctorProfile from './Pages/DoctorDashboard/Profile';
import DoctorClinic from './Pages/DoctorDashboard/Clinic';
import DoctorBilling from './Pages/DoctorDashboard/Billing';
import DoctorSettings from './Pages/DoctorDashboard/Settings';

// New Appointment Management Pages
import AppointmentSearch from './Pages/AppointmentSearch';
import AppointmentHistory from './Pages/AppointmentHistory';
import ReminderSettings from './Pages/ReminderSettings';

// Subscription Components
import { SubscriptionAwareDoctorDashboard } from './components/SubscriptionAwareDoctorDashboard';
import SubscriptionAwareReceptionistDashboard from './components/SubscriptionAwareReceptionistDashboard';
import ClinicalNotes from './Pages/DoctorDashboard/ClinicalNotes';
import EmergencyContacts from './Pages/DoctorDashboard/EmergencyContacts';
import MentalHealthAssessment from './Pages/DoctorDashboard/MentalHealthAssessment';
import PatientDocuments from './Pages/DoctorDashboard/PatientDocuments';
import PatientIntakeForm from './Pages/DoctorDashboard/PatientIntakeForm';
import TreatmentHistory from './Pages/DoctorDashboard/TreatmentHistory';
import TreatmentPlan from './Pages/DoctorDashboard/TreatmentPlan';

// Pharmacist Dashboard Pages
import PharmacistOverview from './Pages/PharmacistDashboard/Overview';
import PrescriptionManagement from './Pages/PharmacistDashboard/PrescriptionManagement';
import MedicationInventory from './Pages/PharmacistDashboard/MedicationInventory';
import InventoryRestocking from './Pages/PharmacistDashboard/InventoryRestocking';
import PharmacistReports from './Pages/PharmacistDashboard/PharmacistReportsAnalytics';
import PharmacistNotifications from './Pages/PharmacistDashboard/PharmacistNotificationPage';
import PharmacistMessaging from './Pages/PharmacistDashboard/PharmacistMessagingHub';

// Receptionist Dashboard Pages
import ReceptionistOverview from './Pages/ReceptionistDashboard/Overview';
import ReceptionistAppointments from './Pages/ReceptionistDashboard/Appointments';
import PatientCheckIn from './Pages/ReceptionistDashboard/CheckInSystem';
import PatientRegistration from './Pages/ReceptionistDashboard/PatientRegistration';
import ReceptionistBilling from './Pages/ReceptionistDashboard/Billing';
import CommunicationHub from './Pages/ReceptionistDashboard/CommunicationHub';
import ReceptionistReports from './Pages/ReceptionistDashboard/ReportsAnalytics';
import ReceptionistNotifications from './Pages/ReceptionistDashboard/NotificationPage';
import TaskManagement from './Pages/ReceptionistDashboard/TaskManagement';

// Public Marketplace Pages
import MarketplaceHomePage from './Pages/MarketplaceHomePage';
import DoctorsListing from './Pages/DoctorsListing';

// P0 Legal Pages
import Terms from './Pages/Legal/Terms';
import Privacy from './Pages/Legal/Privacy';

// P0 Clinic Dashboard
import ClinicOverview from './Pages/ClinicDashboard/ClinicOverview';

// P0 Support & Feedback
import P0Feedback from './Pages/Shared/P0Feedback';
import SupportTickets from './Pages/Shared/SupportTickets';

// Components
import GlobalError from './components/GlobalError';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';

// Context and Theme
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import theme from './theme';

const App = () => {
    const dispatch = useDispatch();

    // Restore auth state from localStorage on app load (fallback if redux-persist hasn't restored yet)
    useEffect(() => {
        dispatch(restoreAuth());
    }, [dispatch]);

    return ( <
        ThemeProvider theme = { theme } >
        <
        CssBaseline / >
        <
        AuthProvider >
        <
        SubscriptionProvider >
        <
        SessionProvider showTimeoutWarning = { true }
        showSessionStatus = { false } >
        <
        Box sx = {
            { display: 'flex', flexDirection: 'column', minHeight: '100vh' }
        } >
        <
        ErrorBoundary >
        <
        Routes > { /* Public Routes */ } <
        Route element = { < PublicLayout / > } >
        <
        Route path = '/'
        element = { < MarketplaceHomePage / > }
        /> <
        Route path = '/home'
        element = { < HomePage / > }
        /> <
        Route path = '/login'
        element = { < Login / > }
        /> <
        Route path = '/signup'
        element = { < Signup / > }
        /> <
        Route path = '/otp-signup'
        element = { < OTPSignup / > }
        /> <
        Route path = '/verify-otp'
        element = { < VerifyOTP / > }
        /> <
        Route path = '/book-appointment'
        element = { < BookAppointment / > }
        /> <
        Route path = '/doctor-onboarding'
        element = { < DoctorOnboarding / > }
        /> <
        Route path = '/enhanced-doctor-onboarding'
        element = { < EnhancedDoctorOnboarding / > }
        /> <
        Route path = '/doctor-onboarding-unified'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['doctor']
            }
            requireVerification = { false } >
            <
            UnifiedDoctorOnboardingFlow / >
            <
            /ProtectedRoute>
        }
        /> <
        Route path = '/welcome/:token'
        element = { < MagicLinkOnboarding / > }
        /> <
        Route path = '/doctors'
        element = { < DoctorsListing / > }
        /> <
        Route path = '/doctor/:doctorId'
        element = { < DoctorPublicProfile / > }
        /> 
        {/* P0 Legal Pages */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        < /Route >

        { /* Magic Link Handler */ } <
        Route path = '/auth/magic-link'
        element = { < MagicLinkHandler / > }
        />

        { /* Email Verification */ } <
        Route path = '/auth/verify-email/:token'
        element = { < EmailVerification / > }
        />

        { /* Password Reset */ } <
        Route path = '/auth/forgot-password'
        element = { < ForgotPassword / > }
        /> <
        Route path = '/auth/reset-password/:token'
        element = { < PasswordReset / > }
        />

        { /* Patient Onboarding Routes */ } <
        Route path = '/onboarding/patient/*'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['patient']
            }
            requireVerification = { false } >
            <
            OnboardingRouter / >
            <
            /ProtectedRoute>
        }
        />

        { /* Unified Patient Onboarding Flow */ } <
        Route path = '/onboarding/patient-unified'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['patient']
            }
            requireVerification = { false } >
            <
            UnifiedOnboardingFlow / >
            <
            /ProtectedRoute>
        }
        />

        { /* Patient Dashboard Routes */ } <
        Route path = '/patient-dashboard/*'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['patient']
            }
            requiredPermissions = {
                ['view_appointments', 'view_prescriptions']
            }
            requireVerification = { true } >
            <
            DashboardLayout role = 'patient' >
            <
            Routes >
            <
            Route index element = { < PatientOverview / > }
            /> <
            Route
            path = 'appointments'
            element = { < PatientAppointments / > }
            /> <
            Route
            path = 'consultation-history'
            element = { < PatientConsultationHistory / > }
            /> <
            Route
            path = 'medical-records'
            element = { < PatientMedicalRecords / > }
            /> <
            Route path = 'billing'
            element = { < PatientBilling / > }
            /> <
            Route path = 'profile'
            element = { < PatientProfile / > }
            /> <
            Route path = 'messages'
            element = { < PatientMessaging / > }
            /> <
            Route path = 'support'
            element = { < PatientSupport / > }
            /> <
            Route path = 'feedback'
            element = { < PatientFeedback / > }
            /> <
            Route
            path = 'notifications'
            element = { < PatientNotifications / > }
            />
            <Route
            path = 'management'
            element = { < PatientManagement / > }
            />
            <Route
            path = 'medical-history'
            element = { < MedicalHistory / > }
            />
            <Route
            path = 'documents'
            element = { < DocumentManagement / > }
            />
            <Route
            path = 'consents'
            element = { < ConsentManagement / > }
            />
            <Route
            path = 'privacy'
            element = { < PrivacyControls / > }
            />
            {/* P0 Support & Feedback */}
            <Route
            path = 'feedback-p0'
            element = { < P0Feedback / > }
            />
            <Route
            path = 'support-tickets'
            element = { < SupportTickets / > }
            />
            < /Routes > <
            /DashboardLayout> < /
            ProtectedRoute >
        }
        />

        { /* Doctor Dashboard Routes */ } <
        Route path = '/doctor-dashboard/*'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['doctor']
            }
            requiredPermissions = {
                ['view_appointments', 'view_patients', 'create_prescriptions']
            }
            requireVerification = { true } >
            <
            DashboardLayout role = 'doctor' >
            <
            SubscriptionAwareDoctorDashboard >
            <
            Routes >
            <
            Route index element = { < DoctorOverview / > }
            /> <
            Route
            path = 'appointments'
            element = { < DoctorAppointments / > }
            /> <
            Route
            path = 'patients'
            element = { < DoctorPatientList / > }
            /> <
            Route
            path = 'patients/:id'
            element = { < DoctorPatientDetails / > }
            /> <
            Route
            path = 'patients/:id/clinical-notes'
            element = { < ClinicalNotes / > }
            /> <
            Route
            path = 'patients/:id/emergency-contacts'
            element = { < EmergencyContacts / > }
            /> <
            Route
            path = 'patients/:id/mental-health-assessment'
            element = { < MentalHealthAssessment / > }
            /> <
            Route
            path = 'patients/:id/documents'
            element = { < PatientDocuments / > }
            /> <
            Route
            path = 'patients/:id/intake-form'
            element = { < PatientIntakeForm / > }
            /> <
            Route
            path = 'patients/:id/treatment-history'
            element = { < TreatmentHistory / > }
            /> <
            Route
            path = 'patients/:id/treatment-plan'
            element = { < TreatmentPlan / > }
            /> <
            Route
            path = 'consultation'
            element = { < DoctorConsultation / > }
            /> <
            Route
            path = 'consultation/:appointmentId/:patientId'
            element = { < DoctorConsultation / > }
            /> <
            Route
            path = 'enhanced-consultation'
            element = { < EnhancedConsultation / > }
            /> <
            Route
            path = 'enhanced-consultation/:appointmentId/:patientId'
            element = { < EnhancedConsultation / > }
            /> <
            Route
            path = 'enhanced-consultation/new'
            element = { < EnhancedConsultation / > }
            /> <
            Route
            path = 'enhanced-consultation/:consultationId'
            element = { < EnhancedConsultation / > }
            /> <
            Route
            path = 'consultation-dashboard'
            element = { < EnhancedConsultationDashboard / > }
            /> <
            Route path = 'notes'
            element = { < DoctorNotes / > }
            /> <
            Route
            path = 'consultation-history'
            element = { < DoctorConsultationHistory / > }
            /> <
            Route
            path = 'consultation-history/:consultationId'
            element = { < DoctorConsultationDetails / > }
            /> <
            Route path = 'prescribe'
            element = { < DoctorPrescribe / > }
            /> <
            Route
            path = 'availability'
            element = { < DoctorAvailability / > }
            /> <
            Route path = 'profile'
            element = { < DoctorProfile / > }
            /> <
            Route path = 'clinic'
            element = { < DoctorClinic / > }
            /> <
            Route path = 'billing'
            element = { < DoctorBilling / > }
            /> <
            Route path = 'settings'
            element = { < DoctorSettings / > }
            /> <
            Route path = 'appointments/search'
            element = { < AppointmentSearch / > }
            /> <
            Route path = 'appointments/history'
            element = { < AppointmentHistory / > }
            /> <
            Route path = 'settings/reminders'
            element = { < ReminderSettings / > }
            />
            <Route
            path = 'management'
            element = { < DoctorManagement / > }
            />
            <Route
            path = 'analytics'
            element = { < DoctorAnalytics / > }
            />
            <Route
            path = 'verification'
            element = { < DoctorVerification / > }
            />
            {/* P0 Clinic Dashboard */}
            <Route
            path = 'clinic-overview'
            element = { < ClinicOverview / > }
            />
            {/* P0 Support & Feedback */}
            <Route
            path = 'feedback'
            element = { < P0Feedback / > }
            />
            <Route
            path = 'support-tickets'
            element = { < SupportTickets / > }
            />
            < /Routes > <
            /SubscriptionAwareDoctorDashboard> < /
            DashboardLayout > <
            /ProtectedRoute>
        }
        />

        { /* Clinic Dashboard Routes */ } <
        Route path = '/clinic-dashboard/*'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['clinic_admin', 'clinic_staff']
            }
            requiredPermissions = {
                ['view_clinic', 'manage_clinic']
            }
            requireVerification = { true } >
            <
            DashboardLayout role = 'clinic' >
            <
            Routes >
            <
            Route index element = { < ClinicManagement / > }
            /> <
            Route
            path = 'analytics'
            element = { < ClinicAnalytics / > }
            />
            {/* P0 Clinic Dashboard */}
            <Route
            path = 'overview'
            element = { < ClinicOverview / > }
            />
            {/* P0 Support & Feedback */}
            <Route
            path = 'feedback'
            element = { < P0Feedback / > }
            />
            <Route
            path = 'support-tickets'
            element = { < SupportTickets / > }
            />
            < /Routes > <
            /DashboardLayout> <
            /ProtectedRoute>
        }
        />

        { /* Notification Dashboard Routes */ } <
        Route path = '/notification-dashboard/*'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['admin', 'clinic_admin', 'doctor']
            }
            requiredPermissions = {
                ['manage_notifications', 'view_notifications']
            }
            requireVerification = { true } >
            <
            DashboardLayout role = 'notification' >
            <
            Routes >
            <
            Route index element = { < NotificationManagement / > }
            /> < /
            Routes > <
            /DashboardLayout> <
            /ProtectedRoute>
        }
        />

        { /* Pharmacist Dashboard Routes */ } <
        Route path = '/pharmacist-dashboard/*'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['pharmacist']
            }
            requiredPermissions = {
                ['view_prescriptions', 'dispense_prescriptions']
            }
            requireVerification = { true } >
            <
            DashboardLayout role = 'pharmacist' >
            <
            Routes >
            <
            Route index element = { < PharmacistOverview / > }
            /> <
            Route
            path = 'inventory'
            element = { < MedicationInventory / > }
            /> <
            Route
            path = 'prescriptions'
            element = { < PrescriptionManagement / > }
            /> <
            Route path = 'stock'
            element = { < InventoryRestocking / > }
            /> <
            Route path = 'reports'
            element = { < PharmacistReports / > }
            /> <
            Route
            path = 'messages'
            element = { < PharmacistMessaging / > }
            /> <
            Route
            path = 'notifications'
            element = { < PharmacistNotifications / > }
            /> < /
            Routes > <
            /DashboardLayout> < /
            ProtectedRoute >
        }
        />

        { /* Receptionist Dashboard Routes */ } <
        Route path = '/receptionist-dashboard/*'
        element = { <
            ProtectedRoute
            allowedRoles = {
                ['receptionist']
            }
            requiredPermissions = {
                ['view_appointments', 'create_appointments', 'view_patients', 'create_patients']
            }
            requireVerification = { true } >
            <
            DashboardLayout role = 'receptionist' >
            <
            SubscriptionAwareReceptionistDashboard >
            <
            Routes >
            <
            Route index element = { < ReceptionistOverview / > }
            /> <
            Route
            path = 'appointments'
            element = { < ReceptionistAppointments / > }
            /> <
            Route path = 'check-in'
            element = { < PatientCheckIn / > }
            /> <
            Route
            path = 'registration'
            element = { < PatientRegistration / > }
            /> <
            Route
            path = 'billing'
            element = { < ReceptionistBilling / > }
            /> <
            Route
            path = 'communication'
            element = { < CommunicationHub / > }
            /> <
            Route
            path = 'tasks'
            element = { < TaskManagement / > }
            /> <
            Route
            path = 'reports'
            element = { < ReceptionistReports / > }
            /> <
            Route
            path = 'notifications'
            element = { < ReceptionistNotifications / > }
            />
            {/* P0 Clinic Dashboard */}
            <Route
            path = 'clinic-overview'
            element = { < ClinicOverview / > }
            />
            {/* P0 Support & Feedback */}
            <Route
            path = 'feedback'
            element = { < P0Feedback / > }
            />
            <Route
            path = 'support-tickets'
            element = { < SupportTickets / > }
            />
            < /Routes > <
            /SubscriptionAwareReceptionistDashboard> < /
            DashboardLayout > <
            /ProtectedRoute>
        }
        />

        { /* Fallback route */ } <
        Route path = '*'
        element = { < Navigate to = '/'
            replace / >
        }
        /> < /
        Routes > <
        /ErrorBoundary>

        <
        ToastContainer position = 'top-right'
        autoClose = { 5000 }
        hideProgressBar = { false }
        newestOnTop closeOnClick rtl = { false }
        pauseOnFocusLoss draggable pauseOnHover /
        >
        <
        GlobalError / >
        <
        /Box> < /
        SessionProvider > < /
        SubscriptionProvider > <
        /AuthProvider> < /
        ThemeProvider >
    );
};

export default App;